// ============================================================
// JsonDialog.js
// Modal that pretty-prints a JSON value. Built on the shared
// <Modal>. Used for "View" links in the audit log, where we
// surface the OldValues / NewValues columns.
//
// If `data` and `sections` are both null/empty, renders an
// explicit "No structured detail recorded." line so the dialog
// is never empty.
//
// Two render modes:
//   - sections: array of { label, value } — each value gets its
//               own <pre> block (used for old + new side-by-side
//               on audit rows). Skips entries whose value is null
//               or undefined.
//   - data:     a single value rendered in one <pre> block.
// ============================================================

import React from 'react';
import Modal from './Modal';
import './JsonDialog.css';

function stringify(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

export default function JsonDialog({ isOpen, onClose, title, data, sections }) {
  const sectionList = Array.isArray(sections)
    ? sections.filter((s) => s.value !== null && s.value !== undefined)
    : [];
  const hasSections = sectionList.length > 0;
  const hasData = data !== null && data !== undefined;
  const isEmpty = !hasSections && !hasData;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="json-dialog">
        {isEmpty && (
          <p className="json-dialog__empty">No structured detail recorded.</p>
        )}
        {hasSections && sectionList.map((s) => (
          <div key={s.label} className="json-dialog__section">
            <div className="json-dialog__section-label">{s.label}</div>
            <pre className="json-dialog__pre">{stringify(s.value)}</pre>
          </div>
        ))}
        {!hasSections && hasData && (
          <pre className="json-dialog__pre">{stringify(data)}</pre>
        )}
      </div>
    </Modal>
  );
}
